



































































































































































































import axios, { CancelTokenSource } from 'axios';
import { Component, Watch } from 'vue-property-decorator';
import CompanyMixin from '@/mixins/CompanyMixin';
import user from '@/store/modules/user';
import QuestionList from '@/components/QuestionList.vue';
import {
  ticGenerateInsights,
  ticGetCompanyWebsite,
  ticGetInsights,
  ticRegenerateInsightQuestion,
} from '@/store/api';

interface ProgressStep {
  label: string;
  minMs: number;
  maxMs: number;
}

const PROGRESS_DURATION_MS = 90000;
const FAST_FORWARD_MS = 500;
const INSIGHTS_POLL_INTERVAL_MS = 2000;
const INSIGHTS_UNDEFINED_POLL_MAX = 3;
const INSIGHTS_GENERATING_MAX_MS = 5 * 60 * 1000;

const PROGRESS_STEPS: ProgressStep[] = [
  { label: 'Analyserar bolaget', minMs: 3000, maxMs: 10000 },
  { label: 'Analyserar hemsidan', minMs: 3000, maxMs: 12000 },
  { label: 'Analyserar branschen', minMs: 3000, maxMs: 10000 },
  { label: 'Sammanställer frågor', minMs: 2000, maxMs: 0 },
];

@Component({ components: { QuestionList } })
export default class CardAIQuestions extends CompanyMixin {
  /** True only while a GET or POST is in flight. Must start false so the first load is not skipped. */
  fetchPending = false;
  /** Quiet fetch of existing insights — not the generate progress UI. */
  initialLoading = true;
  progressActive = false;
  progressMessage = '';
  progressHint = 'Sammanställningen tar ungefär en minut.';
  progressPercent = 0;
  progressNegative = false;
  error: string | null = null;
  stepAResult: any | null = null;
  websiteInput = '';
  websiteDraft = '';
  showWebsiteDialog = false;
  regeneratingId = '';
  private emptyPollCount = 0;
  private undefinedPollStreak = 0;
  private generatingPollSinceMs: number | null = null;
  private showInsightsPanel = true;
  private insightsSettled = false;
  private websiteAutoResolved: string | null = null;
  private websiteEdited = false;
  private websiteUsedForFetch: string | undefined = undefined;
  private cancelSource: CancelTokenSource | null = null;
  private insightsPollTimer: ReturnType<typeof setTimeout> | null = null;
  private progressGeneration = 0;
  private progressTimeouts: Array<ReturnType<typeof setTimeout>> = [];
  private progressRaf: number | null = null;
  private tornDown = false;

  get stepAQuestions(): any[] {
    const r = this.stepAResult;
    if (!r || !Array.isArray(r.questions)) {
      return [];
    }
    return r.questions;
  }

  get batteryMetaParts(): {
    hasContent: boolean;
    bokslut: string | null;
    website: string | null;
  } {
    const r = this.stepAResult;
    if (!r) {
      return { hasContent: false, bokslut: null, website: null };
    }
    const bokslut = this.formatBokslutLabel(r.source_year);
    const website = this.displayWebsiteLabel(r.website || this.websiteInput);
    return {
      hasContent: true,
      bokslut,
      website,
    };
  }

  get companyFetchingData(): boolean {
    return user.fetchingData;
  }

  get insightsWebsiteHref(): string | null {
    const raw =
      (this.stepAResult && this.stepAResult.website) || this.websiteInput || '';
    return this.hrefFromWebsite(raw);
  }

  mounted() {
    this.loadWebsite();
    this.loadSavedOrGenerate();
  }

  beforeDestroy() {
    this.tornDown = true;
    this.stopInsightsPoll();
    this.stopProgressSequence();
    this.cancelPendingFetch();
  }

  @Watch('companyFetchingData')
  onCompanyFetchingData(fetching: boolean) {
    if (!fetching) {
      this.loadSavedOrGenerate();
    }
  }

  cancelPendingFetch() {
    if (this.cancelSource) {
      this.cancelSource.cancel();
      this.cancelSource = null;
    }
  }

  onWebsiteEdited() {
    this.websiteEdited = true;
  }

  openWebsiteDialog() {
    this.websiteDraft =
      (this.stepAResult && this.stepAResult.website) || this.websiteInput || '';
    this.showWebsiteDialog = true;
    this.$nextTick(() => {
      const input = this.$refs.websiteDialogInput as
        | HTMLInputElement
        | undefined;
      if (input) {
        input.focus();
        input.select();
      }
    });
  }

  closeWebsiteDialog() {
    this.showWebsiteDialog = false;
  }

  confirmWebsiteDialog() {
    const next = this.websiteDraft.trim();
    const current = this.normalizeWebsite(
      (this.stepAResult && this.stepAResult.website) || this.websiteInput || '',
    );
    this.showWebsiteDialog = false;
    if (this.normalizeWebsite(next) === current) {
      return;
    }
    this.websiteEdited = true;
    this.websiteInput = next;
    this.stepAResult = null;
    this.error = null;
    this.initialLoading = false;
    this.startBackgroundFetch(next);
    this.startProgressSequence(false);
  }

  onCreateNew() {
    this.stepAResult = null;
    this.error = null;
    this.initialLoading = false;
    this.startBackgroundFetch(this.websiteInput);
    this.startProgressSequence(false);
  }

  onRegenerateQuestion(q: any) {
    const orgNr = this.orgNr();
    if (!orgNr || orgNr === '00' || !q || this.regeneratingId) {
      return;
    }
    const id = q.id || '';
    this.regeneratingId = id || 'pending';
    const keep = this.stepAQuestions.filter(
      (item) => item !== q && item.id !== q.id,
    );
    const rejected = Array.isArray(
      this.stepAResult && this.stepAResult.rejected,
    )
      ? this.stepAResult.rejected
      : [];
    const website =
      this.websiteUsedForFetch !== undefined
        ? this.websiteUsedForFetch
        : this.websiteInput;
    ticRegenerateInsightQuestion(orgNr, {
      website,
      keep,
      replace: q,
      rejected,
      company_summary: this.stepAResult ? this.stepAResult.company_summary : '',
      industry_summary: this.stepAResult
        ? this.stepAResult.industry_summary
        : '',
      webpage_analysis: this.stepAResult
        ? this.stepAResult.webpage_analysis
        : '',
    })
      .then((next) => {
        this.regeneratingId = '';
        if (!next || !this.stepAResult) {
          return;
        }
        const questions = this.stepAQuestions.slice();
        const idx = questions.findIndex(
          (item) => (q.id && item.id === q.id) || item === q,
        );
        if (idx >= 0) {
          questions.splice(idx, 1, next);
        }
        const nextRejected = [q.question]
          .concat(rejected)
          .filter((text, i, arr) => text && arr.indexOf(text) === i)
          .slice(0, 10);
        this.stepAResult = {
          ...this.stepAResult,
          questions,
          rejected: nextRejected,
        };
      })
      .catch(() => {
        this.regeneratingId = '';
      });
  }

  retryFetch() {
    this.stepAResult = null;
    this.error = null;
    this.initialLoading = false;
    this.startBackgroundFetch(this.websiteUsedForFetch);
    this.startProgressSequence(false);
  }

  private hrefFromWebsite(rawInput: string): string | null {
    const raw = (rawInput || '').trim();
    if (!raw) {
      return null;
    }
    let url = raw;
    if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url)) {
      url = `https://${url}`;
    }
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return null;
      }
      return parsed.href;
    } catch {
      return null;
    }
  }

  private loadWebsite() {
    const orgNr = this.orgNr();
    if (!orgNr || orgNr === '00') {
      return;
    }
    ticGetCompanyWebsite(orgNr).then((web) => {
      if (!this.websiteEdited) {
        this.websiteInput = web;
      }
    });
  }

  private loadSavedOrGenerate() {
    if (this.fetchPending || this.insightsPollTimer != null) {
      return;
    }
    const orgNr = this.orgNr();
    if (!orgNr || orgNr === '00') {
      this.error =
        'Välj ett bolag i menyn till vänster för att kunna generera insikter.';
      this.stepAResult = null;
      this.fetchPending = false;
      this.initialLoading = false;
      return;
    }
    // Assume insights exist: stay on result panel, quiet load (no generate spinner).
    this.emptyPollCount = 0;
    this.showInsightsPanel = true;
    this.error = null;
    this.fetchPending = true;
    this.initialLoading = true;
    this.stopProgressSequence();
    this.pollInsightsWhileGenerating();
  }

  private stopInsightsPoll() {
    if (this.insightsPollTimer != null) {
      clearTimeout(this.insightsPollTimer);
      this.insightsPollTimer = null;
    }
  }

  private beginInsightsGeneratingPoll(startedAt?: string) {
    this.stopInsightsPoll();
    this.emptyPollCount = 0;
    this.undefinedPollStreak = 0;
    this.generatingPollSinceMs = null;
    this.stepAResult = null;
    this.error = null;
    this.fetchPending = true;
    this.initialLoading = false;
    if (!this.progressActive) {
      this.startProgressSequence(false, this.elapsedMsSince(startedAt));
    }
    this.pollInsightsWhileGenerating();
  }

  private failInsightsPoll(message: string) {
    this.stopInsightsPoll();
    this.fetchPending = false;
    this.initialLoading = false;
    this.insightsSettled = true;
    this.showInsightsPanel = true;
    this.undefinedPollStreak = 0;
    this.generatingPollSinceMs = null;
    this.error = message;
    this.stopProgressSequence();
  }

  private scheduleInsightsPoll() {
    this.insightsPollTimer = setTimeout(() => {
      this.insightsPollTimer = null;
      this.pollInsightsWhileGenerating();
    }, INSIGHTS_POLL_INTERVAL_MS);
  }

  private generatingPollElapsedMs(startedAt?: string): number {
    if (startedAt) {
      return this.elapsedMsSince(startedAt);
    }
    if (this.generatingPollSinceMs != null) {
      return Math.max(0, Date.now() - this.generatingPollSinceMs);
    }
    return 0;
  }

  private pollInsightsWhileGenerating() {
    const orgNr = this.orgNr();
    if (!orgNr || orgNr === '00') {
      return;
    }
    ticGetInsights(orgNr).then((saved) => {
      if (this.tornDown) {
        return;
      }
      if (saved === undefined) {
        this.undefinedPollStreak += 1;
        if (this.undefinedPollStreak >= INSIGHTS_UNDEFINED_POLL_MAX) {
          this.failInsightsPoll(
            'Kunde inte hämta AI-insikter. Försök igen senare.',
          );
          return;
        }
        this.scheduleInsightsPoll();
        return;
      }
      this.undefinedPollStreak = 0;
      if (saved && saved.generating === true) {
        this.emptyPollCount = 0;
        if (!saved.started_at && this.generatingPollSinceMs == null) {
          this.generatingPollSinceMs = Date.now();
        }
        const generatingElapsed = this.generatingPollElapsedMs(
          saved.started_at,
        );
        if (generatingElapsed >= INSIGHTS_GENERATING_MAX_MS) {
          this.failInsightsPoll(
            'Genereringen tar längre tid än väntat. Försök igen senare.',
          );
          return;
        }
        this.initialLoading = false;
        if (!this.progressActive) {
          this.startProgressSequence(
            false,
            this.elapsedMsSince(saved.started_at),
          );
        }
        this.fetchPending = true;
        this.scheduleInsightsPoll();
        return;
      }
      this.generatingPollSinceMs = null;
      this.stopInsightsPoll();
      this.fetchPending = false;
      if (
        saved &&
        Array.isArray(saved.questions) &&
        saved.questions.length > 0
      ) {
        this.stepAResult = saved;
        this.applyBatteryWebsite(saved);
        this.initialLoading = false;
        this.emptyPollCount = 0;
        this.finishProgressWithFastForward();
        return;
      }
      // Empty: wait briefly for auto-generate to start, then settle on empty result panel.
      this.emptyPollCount += 1;
      if (this.companyFetchingData || this.emptyPollCount < 3) {
        this.fetchPending = true;
        this.scheduleInsightsPoll();
        return;
      }
      this.stepAResult = null;
      this.initialLoading = false;
      this.stopProgressSequence();
    });
  }

  private normalizeWebsite(raw: string): string {
    let d = raw.trim().replace(/\/+$/, '');
    if (!d) {
      return '';
    }
    if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(d)) {
      d = `https://${d}`;
    }
    return d;
  }

  private startBackgroundFetch(websiteOverride?: string) {
    const orgNr = this.orgNr();
    if (!orgNr || orgNr === '00') {
      this.cancelPendingFetch();
      this.fetchPending = false;
      this.error =
        'Välj ett bolag i menyn till vänster för att kunna generera insikter.';
      this.stepAResult = null;
      return;
    }
    this.cancelPendingFetch();
    this.cancelSource = axios.CancelToken.source();
    this.fetchPending = true;
    this.error = null;
    this.websiteUsedForFetch = websiteOverride;
    const cancelToken = this.cancelSource.token;
    ticGenerateInsights(orgNr, cancelToken, websiteOverride)
      .then((data) => {
        if (!this.cancelSource || this.cancelSource.token !== cancelToken) {
          return;
        }
        this.cancelSource = null;
        this.fetchPending = false;
        if (data === undefined) {
          this.stepAResult = null;
          this.error = 'Kunde inte generera insikter. Försök igen senare.';
          this.stopProgressSequence();
        } else {
          this.stepAResult = data;
          this.applyBatteryWebsite(data);
          this.finishProgressWithFastForward();
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) {
          return;
        }
        if (!this.cancelSource || this.cancelSource.token !== cancelToken) {
          return;
        }
        this.cancelSource = null;
        this.fetchPending = false;
        this.stepAResult = null;
        this.error = 'Kunde inte generera insikter. Försök igen senare.';
        this.stopProgressSequence();
      });
  }

  private formatBokslutLabel(sourceYear: number): string | null {
    const dateTo =
      this.overview && typeof this.overview.DATE_TO === 'string'
        ? this.overview.DATE_TO
        : '';
    // DATE_TO is the bokslut period end (YYYY-MM-DD). Prefer that over the
    // stored source year, which for older runs is the period start year and
    // therefore shows up as "Bokslut YYYY".
    if (dateTo.length >= 7) {
      return `Bokslut ${dateTo.slice(0, 7)}`;
    }
    if (sourceYear) {
      return `Bokslut ${sourceYear}`;
    }
    return null;
  }

  private displayWebsiteLabel(raw: string): string | null {
    const trimmed = (raw || '').trim();
    if (!trimmed) {
      return null;
    }
    return trimmed.replace(/^https?:\/\//i, '').replace(/\/+$/, '') || trimmed;
  }

  private applyBatteryWebsite(battery: any) {
    if (!battery || this.websiteEdited) {
      return;
    }
    if (typeof battery.website === 'string' && battery.website.trim()) {
      this.websiteInput = battery.website.trim();
    }
  }

  private elapsedMsSince(startedAt?: string): number {
    if (!startedAt) {
      return 0;
    }
    const t = Date.parse(startedAt);
    if (Number.isNaN(t)) {
      return 0;
    }
    return Math.max(0, Date.now() - t);
  }

  private progressHintForElapsed(elapsedMs: number): string {
    if (elapsedMs >= PROGRESS_DURATION_MS) {
      return 'Det tar lite längre än vanligt — snart klart.';
    }
    if (elapsedMs >= 15000) {
      return 'Insikterna sammanställs i bakgrunden — snart klart.';
    }
    return 'Sammanställningen tar ungefär en minut.';
  }

  private startProgressSequence(useMinDurations: boolean, elapsedMs = 0) {
    this.stopProgressSequence();
    const generation = ++this.progressGeneration;
    this.progressActive = true;
    this.progressNegative = false;
    this.progressHint = this.progressHintForElapsed(elapsedMs);
    const startIndex = this.progressStepIndexForElapsed(elapsedMs);
    this.progressMessage = `${PROGRESS_STEPS[startIndex].label}...`;
    void this.runProgressMessages(generation, startIndex, elapsedMs);
    this.startProgressBar(generation, useMinDurations, elapsedMs);
  }

  private progressStepIndexForElapsed(elapsedMs: number): number {
    let cumulative = 0;
    for (let i = 0; i < PROGRESS_STEPS.length - 1; i += 1) {
      cumulative += PROGRESS_STEPS[i].maxMs;
      if (elapsedMs < cumulative) {
        return i;
      }
    }
    return PROGRESS_STEPS.length - 1;
  }

  private stopProgressSequence() {
    this.progressGeneration += 1;
    this.progressTimeouts.forEach(clearTimeout);
    this.progressTimeouts = [];
    this.cancelProgressRaf();
    this.progressActive = false;
  }

  /** Fast-forward the bar to 100% (~0.5s) before hiding the spinner. */
  private finishProgressWithFastForward() {
    this.fetchPending = false;
    if (!this.progressActive) {
      return;
    }
    const generation = this.progressGeneration;
    this.progressTimeouts.forEach(clearTimeout);
    this.progressTimeouts = [];
    this.cancelProgressRaf();
    // Fill the normal bar to the end (also when we were in overtime).
    this.progressNegative = false;
    this.animateProgressTo(100, FAST_FORWARD_MS, generation, () => {
      this.endProgressSequence(generation);
    });
  }

  private endProgressSequence(generation: number) {
    if (generation !== this.progressGeneration) {
      return;
    }
    this.progressTimeouts.forEach(clearTimeout);
    this.progressTimeouts = [];
    this.cancelProgressRaf();
    this.progressActive = false;
  }

  private cancelProgressRaf() {
    if (this.progressRaf != null) {
      cancelAnimationFrame(this.progressRaf);
      this.progressRaf = null;
    }
  }

  private async runProgressMessages(
    generation: number,
    startIndex = 0,
    elapsedMs = 0,
  ) {
    let spentBefore = 0;
    for (let i = 0; i < startIndex; i += 1) {
      spentBefore += PROGRESS_STEPS[i].maxMs;
    }
    for (let i = startIndex; i < PROGRESS_STEPS.length; i += 1) {
      if (generation !== this.progressGeneration) {
        return;
      }
      this.progressMessage = `${PROGRESS_STEPS[i].label}...`;
      if (i === PROGRESS_STEPS.length - 1) {
        return;
      }
      const stepMs = PROGRESS_STEPS[i].maxMs;
      const alreadyInStep =
        i === startIndex ? Math.max(0, elapsedMs - spentBefore) : 0;
      const waitMs = Math.max(0, stepMs - alreadyInStep);
      await this.delay(waitMs);
      if (generation !== this.progressGeneration) {
        return;
      }
    }
  }

  private startProgressBar(
    generation: number,
    alreadyDone: boolean,
    elapsedMs = 0,
  ) {
    if (alreadyDone) {
      this.progressPercent = Math.min(
        100,
        (elapsedMs / PROGRESS_DURATION_MS) * 100,
      );
      this.animateProgressTo(100, FAST_FORWARD_MS, generation, () => {
        this.endProgressSequence(generation);
      });
      return;
    }

    if (elapsedMs >= PROGRESS_DURATION_MS) {
      this.progressNegative = true;
      this.progressPercent = 0;
      this.progressHint = this.progressHintForElapsed(elapsedMs);
      this.startOvertimeBar(generation);
      return;
    }

    const startedAt = performance.now() - elapsedMs;
    this.progressPercent = (elapsedMs / PROGRESS_DURATION_MS) * 100;
    const tick = (now: number) => {
      if (generation !== this.progressGeneration) {
        return;
      }
      if (!this.fetchPending) {
        this.animateProgressTo(100, FAST_FORWARD_MS, generation, () => {
          this.endProgressSequence(generation);
        });
        return;
      }
      const elapsed = now - startedAt;
      if (elapsed >= PROGRESS_DURATION_MS) {
        this.progressNegative = true;
        this.progressPercent = 0;
        this.progressHint = this.progressHintForElapsed(elapsed);
        this.startOvertimeBar(generation);
        return;
      }
      this.progressPercent = (elapsed / PROGRESS_DURATION_MS) * 100;
      this.progressRaf = requestAnimationFrame(tick);
    };
    this.progressRaf = requestAnimationFrame(tick);
  }

  private startOvertimeBar(generation: number) {
    const startedAt = performance.now();
    const tick = (now: number) => {
      if (generation !== this.progressGeneration) {
        return;
      }
      if (!this.fetchPending) {
        this.animateProgressTo(100, FAST_FORWARD_MS, generation, () => {
          this.endProgressSequence(generation);
        });
        return;
      }
      const elapsed = now - startedAt;
      if (elapsed >= PROGRESS_DURATION_MS) {
        this.progressPercent = 100;
        void this.waitUntilFetchDone(generation).then(() => {
          if (generation !== this.progressGeneration) {
            return;
          }
          this.endProgressSequence(generation);
        });
        return;
      }
      this.progressPercent = (elapsed / PROGRESS_DURATION_MS) * 100;
      this.progressRaf = requestAnimationFrame(tick);
    };
    this.progressRaf = requestAnimationFrame(tick);
  }

  private animateProgressTo(
    target: number,
    durationMs: number,
    generation: number,
    onDone: () => void,
  ) {
    const from = this.progressPercent;
    const startedAt = performance.now();
    const step = (now: number) => {
      if (generation !== this.progressGeneration) {
        return;
      }
      const t = Math.min(1, (now - startedAt) / durationMs);
      const eased = t * t * (3 - 2 * t);
      this.progressPercent = from + (target - from) * eased;
      if (t < 1) {
        this.progressRaf = requestAnimationFrame(step);
        return;
      }
      this.progressPercent = target;
      onDone();
    };
    this.progressRaf = requestAnimationFrame(step);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const id = window.setTimeout(resolve, ms);
      this.progressTimeouts.push(id);
    });
  }

  private waitUntilFetchDone(generation: number): Promise<void> {
    return new Promise((resolve) => {
      const poll = () => {
        if (generation !== this.progressGeneration) {
          return;
        }
        if (!this.fetchPending) {
          resolve();
          return;
        }
        const id = window.setTimeout(poll, 50);
        this.progressTimeouts.push(id);
      };
      poll();
    });
  }
}
